import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../../components/ui/alert'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)
    try {
        const resetUrl = `${window.location.origin}/#/auth/reset-password`

        console.log('Using direct reset URL:', resetUrl)

        const { error } = await supabase.auth.resetPasswordForEmail(
          email,
          {
            redirectTo: resetUrl
          }
          )

      if (error) throw error
      setIsSuccess(true)
      toast({
        title: 'Success',
        description: 'Password reset instructions have been sent to your email',
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`flex min-h-screen items-center justify-center ${getThemeClass('gradients.primary')}`}>
      <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
        <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>Reset Password</h1>
        {isSuccess ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                Check your email for the password reset link. The link will expire in 1 hour.
              </AlertDescription>
            </Alert>
            <Link to="/auth/login">
              <Button
                type="button"
                className={`w-full ${getThemeClass('components.button.secondary')}`}
              >
                Back to Login
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className={`w-full ${getThemeClass('components.button.primary')}`}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Instructions'}
            </Button>
            <Link to="/auth/login">
              <Button
                type="button"
                variant="link"
                className="w-full"
              >
                Back to Login
              </Button>
            </Link>
          </form>
        )}
      </Card>
    </div>
  )
}
